import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  TablePagination,
  CircularProgress,
  Button,
  Box,
  TextField,
  Grid,
  InputAdornment,
  TableSortLabel, // Import TextField for search functionality
} from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import UserService from "../../Services/UserList";
import * as XLSX from "xlsx"; // Import the xlsx library
import { saveAs } from "file-saver"; // Import file-saver for saving files
import Loader from "../utils/Loader";
import TableChartIcon from "@mui/icons-material/TableChart";
import { Search } from "@mui/icons-material";
import { fetchHelpdesk } from "../../redux/slices/helpdeskSlice";
import { useDispatch, useSelector } from "react-redux";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import { ClickableTableLink } from "./ClickableTableLink";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import { FY_YEAR_OPTIONS } from "../utils/FilterOptions";

const HelpDeskList = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { helpdeskData, count, status } = useSelector(state => state.helpdesk);
  const counts = count || 0;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // For Material-UI pagination
  const [rowsPerPage, setRowsPerPage] = useState(10); // Number of items per page
  const [searchQuery, setSearchQuery] = useState(""); // Search query state
  const [sortBy, setSortBy] = useState({
    sort_by: "created_at",
    order: "desc",
  });

  const [month, setMonth] = useState(() => {
    if (state && state.months) return state.months;
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });

  const [year, setYear] = useState(() => {
    if (state?.years?.length > 0) {
      const targetYrs = state.years.map(String);
      return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });

  const hasValidFilters = Boolean(year?.length > 0 && month?.length > 0);

  useEffect(() => {
    if (!hasValidFilters) {
      return;
    }
    dispatch(fetchHelpdesk({
      search: searchQuery,
      page,
      rowsPerPage,
      sort_by: sortBy.sort_by,
      sort_order: sortBy.order,
      month: month,
      year: year?.map((y) => y.value),
    }))
  }, [searchQuery, page, rowsPerPage, sortBy, month, year, hasValidFilters])

  const change_status = async (e, id) => {
    try {
      const status = e.target.value;
      let mergedData = { status, id };
      setLoading(true);
      const response = await UserService.helpdesk(mergedData);
      setLoading(false);
      if (hasValidFilters) {
        dispatch(fetchHelpdesk({
          search: searchQuery,
          page,
          rowsPerPage,
          sort_by: sortBy.sort_by,
          sort_order: sortBy.order,
          month: month,
          year: year?.map((y) => y.value),
        }))
      }
    } catch (error) {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change and filter data
  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
  };

  // Excel Download Function
  const handleDownloadExcel = () => {
    const excelData = helpdeskData?.map((item, idx) => ({
      "Sr.No": idx + 1,
      "Ticket ID": item.id,
      "User Type": item.user_type,
      "EMP Code/GST No.": item.emp_code,
      "Username": item.username,
      "Email": item.email,
      "Category": item.categoery,
      "Message": item.suggestion,
      "Status": item.status,
      "Date": item.created_at ? new Date(item.created_at).toLocaleDateString("en-GB") : "",
      "Time": item.updated_at
        ? new Date(item.updated_at).toLocaleString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Help Desk");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "HelpDeskList.xlsx");
  };

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const filterConfigs = [
    { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
  ];

  const handleMonthChange = (newMonths) => {
    setMonth(newMonths);
    if (newMonths?.length > 0 && year?.length === 0) {
      const currentYrStr = String(new Date().getFullYear());
      setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    }
    setPage(0);
  };

  const handleFilterChange = (filterId, nextSelected) => {
    if (filterId === "year") {
      setYear(nextSelected);
      if (nextSelected.length === 0) {
        setMonth([]);
      } else if (month?.length === 0) {
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
      }
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setPage(0);
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
    const currentYrStr = String(new Date().getFullYear());
    setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
  };

  return (
    <MainLayout title={'Helpdesk'}>
      
      <Box sx={{ p: 3 }}>
        {/* Search Bar and Excel Download Button */}
        <FilterBar2
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          selectedMonth={month}
          setSelectedMonth={handleMonthChange}
          searchTerm={searchQuery}
          setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
          onClearFilters={handleClearFilters}
          actions={
            <Button
              variant="contained"
              startIcon={<TableChartIcon />}
              onClick={handleDownloadExcel}
              sx={{
                textTransform: "none",
                bgcolor: "#2e7d32",
                py: "2px",
                px: "22px",
                borderRadius: 12,
                fontSize: "12px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                boxShadow: "none",
                "&:hover": { bgcolor: "#1b5e20", boxShadow: "none" },
              }}
            >
              Excel
            </Button>
          }
        />

        {/* Table with white background */}
        <TableContainer sx={{ backgroundColor: "white", maxHeight: "calc(100vh - 240px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ background: "#04121f", color: "white", "& th": { backgroundColor: "#04121f", color: "white" } }}>
              <TableRow>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <strong>Sr.No</strong>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "id"}
                    direction={
                      sortBy.sort_by === "id" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("id")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    <strong>Ticket ID</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <strong>User Type</strong>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <strong>EMP Code/GST No.</strong>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "username"}
                    direction={
                      sortBy.sort_by === "username" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("username")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    <strong>Username</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <strong>Email</strong>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "categoery"}
                    direction={
                      sortBy.sort_by === "categoery" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("categoery")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >

                    <strong>Category</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <strong>Message</strong>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "status"}
                    direction={
                      sortBy.sort_by === "status" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("status")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >
                    <strong>Status</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "created_at"}
                    direction={
                      sortBy.sort_by === "created_at" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("created_at")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >
                    <strong>Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: "white", whiteSpace: "nowrap", }}>
                  <strong>Time</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (!hasValidFilters) {
                  return (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  );
                }
                return helpdeskData?.length > 0 ? (
                  helpdeskData.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.user_type}</TableCell>
                      <TableCell>{item.emp_code}</TableCell>
                      <TableCell><ClickableTableLink path="/admin/userlist" displayValue={item.username} searchValue={item.emp_code} date={item.user_created_at ? item.user_created_at.substring(0, 4) : null} /></TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.categoery}</TableCell>
                      <TableCell>{item.suggestion}</TableCell>
                      <TableCell>
                        <Select
                          value={item.status}
                          onChange={(e) => change_status(e, item.id)}
                          fullWidth
                        >
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="close">Close</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        {item ? new Date(item.created_at).toLocaleDateString("en-GB") : ""}
                      </TableCell>
                      <TableCell align="center">
                        {new Date(item.updated_at).toLocaleString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (status === 'pending' || loading) ? (
                  <TableBodySkeleton cols={11} rows={10} />
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No tickets found
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={hasValidFilters ? counts : 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={hasValidFilters ? [10, 20, 30, 100, 500, counts || 0] : [10, 20, 30, 100, 500]}
        />
      </Box>
    </MainLayout>
  );
};

export default HelpDeskList;
